import {useReadContract, useAccount, useBlockNumber} from "wagmi";
import {formatEther, type Address} from "viem";
import {SMELT_ABI, RITUAL_WALLET_ABI} from "@/lib/abi";
import {SMELT_CONTRACT, SYSTEM} from "@/lib/config";

/** Read a Smelt filing by ticker + period. */
export function useFiling(ticker?: string, period?: string) {
  return useReadContract({
    address: SMELT_CONTRACT,
    abi: SMELT_ABI,
    functionName: "getFiling",
    args: ticker && period ? [ticker, period] : undefined,
    query: {enabled: !!ticker && !!period},
  });
}

/** Number of filings stored. */
export function useFilingCount() {
  return useReadContract({
    address: SMELT_CONTRACT,
    abi: SMELT_ABI,
    functionName: "filingCount",
  });
}

/** Filing key at index i (for enumeration). */
export function useFilingKeyAt(i: number) {
  return useReadContract({
    address: SMELT_CONTRACT,
    abi: SMELT_ABI,
    functionName: "filingKeyAt",
    args: [BigInt(i)],
    query: {enabled: i >= 0},
  });
}

/** Filing by raw key (bytes32). */
export function useFilingByKey(key?: `0x${string}`) {
  return useReadContract({
    address: SMELT_CONTRACT,
    abi: SMELT_ABI,
    functionName: "filingByKey",
    args: key ? [key] : undefined,
    query: {enabled: !!key},
  });
}

/** Smelt query fee (wei). */
export function useQueryFee() {
  return useReadContract({
    address: SMELT_CONTRACT,
    abi: SMELT_ABI,
    functionName: "queryFee",
  });
}

/** Pull-payment credits owed to the connected wallet. */
export function useMyCredits() {
  const {address} = useAccount();
  return useReadContract({
    address: SMELT_CONTRACT,
    abi: SMELT_ABI,
    functionName: "credits",
    args: address ? [address] : undefined,
    query: {enabled: !!address, refetchInterval: 12_000},
  });
}

/** Smelt contract's own RitualWallet balance + lock (UI surfaces this). */
export function useSmeltWalletState() {
  const {data: blockNumber } = useBlockNumber({query: {refetchInterval: 15_000}});
  const balance = useReadContract({
    address: SYSTEM.RITUAL_WALLET as Address,
    abi: RITUAL_WALLET_ABI,
    functionName: "balanceOf",
    args: [SMELT_CONTRACT],
    query: {refetchInterval: 15_000},
  });
  const lockUntil = useReadContract({
    address: SYSTEM.RITUAL_WALLET as Address,
    abi: RITUAL_WALLET_ABI,
    functionName: "lockUntil",
    args: [SMELT_CONTRACT],
    query: {refetchInterval: 15_000},
  });
  return {
    balanceWei: balance.data ?? 0n,
    balanceRitual: balance.data ? formatEther(balance.data) : "0",
    lockUntilBlock: lockUntil.data ?? 0n,
    currentBlock: blockNumber ?? 0n,
    isLoading: balance.isLoading || lockUntil.isLoading,
  };
}
